import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class BookmarkService {
  private bookmarkedMarkets = new BehaviorSubject<Set<string>>(new Set());
  public bookmarkedMarkets$ = this.bookmarkedMarkets.asObservable();

  constructor(
    private api: ApiService,
    private auth: AuthService
  ) {
    this.loadFromLocalStorage();
  }

  // Load bookmarks from localStorage
  private loadFromLocalStorage(): void {
    const stored = localStorage.getItem('bookmarkedMarkets');
    if (stored) {
      try {
        const array = JSON.parse(stored);
        this.bookmarkedMarkets.next(new Set(array));
      } catch (error) {
        console.error('Error loading bookmarks from localStorage:', error);
      }
    }
  }

  // Save bookmarks to localStorage
  private saveToLocalStorage(): void {
    const array = Array.from(this.bookmarkedMarkets.value);
    localStorage.setItem('bookmarkedMarkets', JSON.stringify(array));
  }

  // Check if a market is bookmarked
  isBookmarked(marketId: any): boolean {
    if (!marketId) return false;
    return this.bookmarkedMarkets.value.has(String(marketId));
  }

  // Toggle bookmark for a market
  toggleBookmark(marketId: any): void {
    if (!this.auth.isLoggedIn()) {
      this.auth.sendOperationSignal('login');
      return;
    }

    const id = String(marketId);
    const current = this.bookmarkedMarkets.value;
    const updated = new Set(current);

    this.api.toggleBookmark(id).subscribe({
      next: (res) => {
        if (res.data === true) {
          updated.add(id);
        } else {
          updated.delete(id);
        }
        this.bookmarkedMarkets.next(updated);
        this.saveToLocalStorage();
      },
      error: (err) => {
        console.error('Error toggling bookmark:', err);
      }
    });
  }

  // Add bookmark
  addBookmark(marketId: any): void {
    const id = String(marketId);
    const updated = new Set(this.bookmarkedMarkets.value);
    updated.add(id);
    this.bookmarkedMarkets.next(updated);
    this.saveToLocalStorage();
  }

  // Remove bookmark
  removeBookmark(marketId: any): void {
    const id = String(marketId);
    const updated = new Set(this.bookmarkedMarkets.value);
    updated.delete(id);
    this.bookmarkedMarkets.next(updated);
    this.saveToLocalStorage();
  }

  // Get all bookmarked markets
  getBookmarkedMarkets(): string[] {
    return Array.from(this.bookmarkedMarkets.value);
  }

  // Clear all bookmarks
  clearAllBookmarks(): void {
    this.bookmarkedMarkets.next(new Set());
    localStorage.removeItem('bookmarkedMarkets');
  }
}
